import { memo, useEffect, useMemo, useState } from 'react'
import { Autocomplete, Box, Dialog, DialogContent, Divider, Grid, Stack, TextField } from '@mui/material';
import Section from 'components/common/Section/Section';
import ProductInfoItem from '../ProductInfoItem/ProductInfoItem';
import CustomLabel from 'components/common/CustomLabel/CustomLabel';
import CustomText from 'components/common/CustomText/CustomText';
import TagProductsModal from 'components/modals/TagProducts/TagProductsModal';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput';
import { useForm } from 'react-hook-form';
import { useProductContext } from 'context/ProductContext';
import { useUpdateProductLeafCategory } from 'hooks/useProduct';
import { useGetLeafCategories } from 'hooks/useRawProductCategoryReview';
import { LeafCategoryOption } from 'services/rawProductService';
import { snackbarService } from 'services/snackbarService';
import { colors } from 'styles/colors';
import SaveIcon from 'icons/SaveIcon';
import PencilIcon from 'icons/PencilIcon';
import XIcon from 'icons/XIcon';

interface TagFormValues {
  tagEn: string;
  tagAr: string;
}

// Translations reach the page keyed by language in some places and as a plain
// list in others, so both are read here rather than at every call site.
const nameOf = (item: any, language: 'en' | 'ar') => {
  const translations = item?.translations;
  if (!translations) return '';

  if (Array.isArray(translations)) {
    return translations.find((entry: any) => entry?.languageCode === language)?.name ?? '';
  }

  return translations[language]?.name ?? '';
};

// One field for the whole category, the way a raw product's is picked. The
// taxonomy is settled and every leaf carries its own ancestry, so naming each
// level on the way down was a chance to write a chain the tree does not have -
// and a product only ever hangs off the leaf anyway.
function ProductCategoryDetails() {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();
  const [leaf, setLeaf] = useState<LeafCategoryOption | null>(null);

  const { data: leafCategories = [] } = useGetLeafCategories();

  // Counted on the server, where the products are. One before it answers is this
  // product itself, which is true of every tag a product has.
  const tagUse = product.tagProductCount ?? 1;
  const [tagProductsOpen, setTagProductsOpen] = useState<boolean>(false);
  const [tagChoiceOpen, setTagChoiceOpen] = useState<boolean>(false);

  const { register, reset, getValues } = useForm<TagFormValues>({
    defaultValues: { tagEn: '', tagAr: '' },
  });

  // Root to leaf, as the product carries it - the same reading as a picker
  // option's path, so the two say the same thing about the same product.
  const currentPath = useMemo(() => {
    const categories = product.categories ?? [];
    return {
      en: categories.map((category: any) => nameOf(category, 'en')).filter(Boolean).join(' > '),
      ar: categories.map((category: any) => nameOf(category, 'ar')).filter(Boolean).join(' > '),
    };
  }, [product.categories]);

  const currentLeafId = useMemo(() => {
    const categories = product.categories ?? [];
    return categories.length ? categories[categories.length - 1]?.id ?? null : null;
  }, [product.categories]);

  useEffect(() => {
    setLeaf(leafCategories.find((option) => option.id === currentLeafId) ?? null);
  }, [leafCategories, currentLeafId]);

  useEffect(() => {
    reset({
      tagEn: nameOf(product.tag, 'en'),
      tagAr: nameOf(product.tag, 'ar'),
    });
  }, [product.tag, reset]);

  const { mutate: updateMutation, isLoading } = useUpdateProductLeafCategory(
    () => {
      setIsEditing(false);
      setTagChoiceOpen(false);
      snackbarService.showSnackbar('Product Category has been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => { console.log('error', err) },
  )

  // What is about to be sent, tag and all. Built once and used twice: the save
  // itself, and the save made again with an answer once the question below has
  // been asked.
  const payload = (scope?: 'all' | 'this') => {
    const { tagEn, tagAr } = getValues();

    return {
      id: product.id,
      leafCategoryId: leaf?.id ?? '',
      // Left off when either half is missing: a tag is a pair, and saving one
      // side alone is how the two languages drift apart.
      ...(tagEn.trim() && tagAr.trim()
        ? {
          tag: {
            translations: {
              en: { name: tagEn.trim(), languageCode: 'en' },
              ar: { name: tagAr.trim(), languageCode: 'ar' },
            },
          },
          ...(scope ? { tagScope: scope } : {}),
        }
        : {}),
    };
  };

  // Whether the two boxes say something other than what the tag says now. Case
  // and the spaces around it are not a difference, the same reading the server
  // goes by.
  const tagRetyped = () => {
    const { tagEn, tagAr } = getValues();
    const same = (left: string, right: string) =>
      left.trim().toLowerCase() === right.trim().toLowerCase();

    if (!tagEn.trim() || !tagAr.trim()) return false;

    return !(same(tagEn, nameOf(product.tag, 'en')) && same(tagAr, nameOf(product.tag, 'ar')));
  };

  const handleSave = () => {
    if (!leaf) {
      snackbarService.showSnackbar('Pick a category first', 'error');
      return;
    }

    // New wording on a tag other products wear can mean two things - the tag is
    // called this now, or this product belongs under a different tag - and
    // nothing here can tell them apart, so it is asked rather than assumed.
    if (tagRetyped() && tagUse > 1) {
      setTagChoiceOpen(true);
      return;
    }

    // Only this product wears it, so rewording it is rewording nothing else.
    updateMutation(payload(tagRetyped() ? 'all' : undefined));
  }

  const handleCancel = () => {
    setLeaf(leafCategories.find((option) => option.id === currentLeafId) ?? null);
    reset({
      tagEn: nameOf(product.tag, 'en'),
      tagAr: nameOf(product.tag, 'ar'),
    });
    setIsEditing(false);
  }

  return (
    <>
      <Section
        title={'Product Category'}
      buttons={[
        ...(isEditing
          ? [
            {
              icon: <SaveIcon size={20} color={colors.success} />,
              title: 'Save',
              onClick: () => handleSave(),
              loading: isLoading,
            },
            {
              icon: <XIcon size={15} color={colors.error} />,
              title: 'Cancel',
              onClick: () => handleCancel(),
              disabled: isLoading,
            },
          ]
          : [
            {
              icon: <PencilIcon size={20} color={colors.primary} />,
              title: 'Edit',
              onClick: () => setIsEditing(true),
              disabled: isLoading,
            }
          ]),
      ]}
      content={
        isEditing ? (
          <>
            <Grid size={{ xs: 12 }}>
              <Box>
                <CustomLabel label={'Category'} required />
                <Autocomplete
                  options={leafCategories}
                  value={leaf}
                  onChange={(_event, value) => setLeaf(value)}
                  getOptionLabel={(option) => option.pathEN}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="Search categories" />
                  )}
                />
              </Box>
            </Grid>

            <Grid size={12}>
              <Divider />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={'tagEn'}
                label={'Tag English Name'}
                placeholder={'English tag name'}
                register={register}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <CustomFormTextInput
                name={'tagAr'}
                label={'Tag Arabic Name'}
                placeholder={'Arabic tag name'}
                register={register}
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <ProductInfoItem label={'Category English Path'} value={currentPath.en || null} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <ProductInfoItem label={'Category Arabic Path'} value={currentPath.ar || null} />
            </Grid>

            <Grid size={12}>
              <Divider />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <ProductInfoItem label={'Tag English Name'} value={nameOf(product.tag, 'en') || null} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <ProductInfoItem label={'Tag Arabic Name'} value={nameOf(product.tag, 'ar') || null} />
            </Grid>

            {/* A tag is shared, and its wording is not this product's alone to
                change - so the count is here beside it, and it opens the list of
                the products that would be reworded with it. */}
            {product.tag?.id && (
              <Grid size={12}>
                <Divider sx={{ mb: 2 }} />
                <CustomText text={'Used By'} fontSize={15} fontWeight={600} />
                <Box
                  component="button"
                  type="button"
                  onClick={() => setTagProductsOpen(true)}
                  sx={{
                    p: 0,
                    border: 0,
                    background: 'none',
                    cursor: 'pointer',
                    display: 'block',
                    textAlign: 'left',
                  }}
                >
                  <CustomText
                    text={tagUse === 1 ? 'This product only' : `${tagUse} products`}
                    fontSize={15}
                    fontWeight={500}
                    color={colors.link}
                  />
                </Box>
              </Grid>
            )}
          </>
        )
        }
      />

      <TagProductsModal
        open={tagProductsOpen}
        tagId={product.tag?.id ?? null}
        tagName={nameOf(product.tag, 'en')}
        onClose={() => setTagProductsOpen(false)}
      />

      <Dialog open={tagChoiceOpen} onClose={() => !isLoading && setTagChoiceOpen(false)}>
        <DialogContent>
          <CustomText text={'This tag is shared'} fontSize={18} fontWeight={700} />
          <Box mt={0.5} mb={2.5}>
            <CustomText
              text={`${tagUse} products wear it. Rewording the tag rewords it for all of them; the other way leaves them as they are and moves this product on its own.`}
              fontSize={13}
              color={colors.gray}
            />
          </Box>

          <Stack direction="row" gap={1.5}>
            <CustomButton
              text={`Rename for all ${tagUse}`}
              onClick={() => updateMutation(payload('all'))}
              isLoading={isLoading}
            />
            <CustomButton
              text={'Only this product'}
              variant="outlined"
              onClick={() => updateMutation(payload('this'))}
              disabled={isLoading}
            />
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default memo(ProductCategoryDetails);
